import React, { lazy, Suspense } from "react";
import { Toaster } from "react-hot-toast";
import { Route, Routes } from "react-router-dom";
import "./App.css";
import AuthProvider from "./contexts/AuthProvider";
import PreLoader from "./Pages/SharedComponents/PreLoader/PreLoader";
import ScrollToTop from "./Pages/SharedComponents/ScrollToTop/ScrollToTop";

/* ── Route-level lazy chunks ── */
const Home = lazy(() => import("./Pages/HomePage/Home/Home"));
const Login = lazy(() => import("./Pages/AuthPage/LogIn/Login"));
const AboutUs = lazy(() => import("./Pages/AboutUsPage/AboutUs/AboutUs"));
const Register = lazy(() => import("./Pages/AuthPage/Register/Register"));
const Dashboard = lazy(
  () => import("./Pages/DashboardPage/Dashboard/Dashboard"),
);
const ContactUs = lazy(() => import("./Pages/ContactUsPage/ContactUs"));
const Categories = lazy(
  () => import("./Pages/CategoriesPage/Categories/Categories"),
);
const Publications = lazy(
  () => import("./Pages/PublicationsPage/Publications/Publications"),
);
const SinglePublication = lazy(
  () => import("./Pages/PublicationsPage/SinglePublication/SinglePublication"),
);
const CheckOutPage = lazy(() => import("./Pages/CheckOutPage/CheckOutPage"));
const NotFoundPage = lazy(() => import("./Pages/NotFoundPage/NotFoundPage"));
const PrivacyPolicy = lazy(
  () => import("./Pages/PrivacyPolicyPage/PrivacyPolicy"),
);
const ResetPassword = lazy(
  () => import("./Pages/AuthPage/ResetPassword/ResetPassword"),
);
const TermsAndCondition = lazy(
  () => import("./Pages/TermsAndConditionPage/TermsAndCondition"),
);
const OffersPage = lazy(() => import("./Pages/OffersPage/OffersPage"));
const OrderSuccessPage = lazy(
  () => import("./Pages/OrderSuccessPage/OrderSuccessPage"),
);
const ProductDetails = lazy(
  () => import("./Pages/ProductDetails/ProductDetails"),
);

/* ── Dashboard sub-route chunks (only loaded when /dashboard is visited) ── */
const IsAdmin = lazy(() => import("./Pages/AuthPage/IsAdmin/IsAdmin"));
const RequiredAuth = lazy(
  () => import("./Pages/AuthPage/RequiredAuth/RequiredAuth"),
);
const SingleCategory = lazy(
  () => import("./Pages/CategoriesPage/SingleCategory/SingleCategory"),
);
const AddAdmin = lazy(() => import("./Pages/DashboardPage/AddAdmin/AddAdmin"));
const AddProduct = lazy(
  () => import("./Pages/DashboardPage/AddProduct/AddProduct"),
);
const AddReview = lazy(
  () => import("./Pages/DashboardPage/AddReview/AddReview"),
);
const ManageOrders = lazy(
  () => import("./Pages/DashboardPage/ManageOrders/ManageOrders"),
);
const ManageProduct = lazy(
  () => import("./Pages/DashboardPage/ManageProduct/ManageProduct"),
);
const ManageReviews = lazy(
  () => import("./Pages/DashboardPage/ManageReviews/ManageReviews"),
);
const MyOrders = lazy(() => import("./Pages/DashboardPage/MyOrders/MyOrders"));
const Profile = lazy(() => import("./Pages/DashboardPage/Profile/Profile"));
const AddOffer = lazy(() => import("./Pages/DashboardPage/AddOffer/AddOffer"));
const ManageOffers = lazy(
  () => import("./Pages/DashboardPage/ManageOffers/ManageOffers"),
);
const ManageBanner = lazy(
  () => import("./Pages/DashboardPage/ManageBanner/ManageBanner"),
);
const PixelSetup = lazy(
  () => import("./Pages/DashboardPage/PixelSetup/PixelSetup"),
);
const ManageAuthors = lazy(
  () => import("./Pages/DashboardPage/ManageAuthors/ManageAuthors"),
);
const ManagePublicationsAdmin = lazy(
  () => import("./Pages/DashboardPage/ManagePublications/ManagePublications"),
);
const ManageCategoriesAdmin = lazy(
  () => import("./Pages/DashboardPage/ManageCategories/ManageCategories"),
);
const AuthorsPage = lazy(
  () => import("./Pages/AuthorsPage/Authors/Authors"),
);
const SingleAuthorPage = lazy(
  () => import("./Pages/AuthorsPage/SingleAuthor/SingleAuthor"),
);

/* ── Below-fold UI & analytics chunks ── */
const BackToTopButton = lazy(
  () => import("./Pages/SharedComponents/BackToTopButton/BackToTopButton"),
);
const FloatingSupport = lazy(
  () => import("./Pages/SharedComponents/FloatingSupport/FloatingSupport"),
);
const PixelTracker = lazy(
  () => import("./Pages/SharedComponents/PixelTracker/PixelTracker"),
);

function App() {
  return (
    <AuthProvider>
      <Suspense fallback={null}>
        <BackToTopButton />
      </Suspense>
      <Toaster />
      <Suspense fallback={null}>
        <FloatingSupport />
      </Suspense>
      {/* Analytics/tracking loads after main content — never blocks LCP */}
      <Suspense fallback={null}>
        <PixelTracker />
      </Suspense>
      <Suspense fallback={<PreLoader />}>
        <ScrollToTop>
          <Routes>
            <Route path="/" element={<Home />} />
            <Route path="/home" element={<Home />} />
            <Route path="/about-us" element={<AboutUs />} />
            <Route path="/contact-us" element={<ContactUs />} />
            <Route path="/privacy-policy" element={<PrivacyPolicy />} />
            <Route
              path="/terms-and-conditions"
              element={<TermsAndCondition />}
            />
            <Route path="/offers" element={<OffersPage />} />
            <Route path="/login" element={<Login />} />
            <Route path="/register" element={<Register />} />
            <Route path="/reset-password" element={<ResetPassword />} />
            <Route path="/product/:id" element={<ProductDetails />} />
            <Route path="/categories" element={<Categories />}>
              <Route
                path="/categories/:searchString"
                element={<SingleCategory />}
              />
            </Route>
            <Route path="/publications" element={<Publications />} />
            <Route path="/publications/:searchString" element={<SinglePublication />} />
            <Route path="/authors" element={<AuthorsPage />} />
            <Route path="/author/:id" element={<SingleAuthorPage />} />
            <Route path="/checkout" element={<CheckOutPage />} />
            <Route
              path="/order-success/:orderId"
              element={<OrderSuccessPage />}
            />
            <Route
              path="/dashboard"
              element={
                <RequiredAuth>
                  <Dashboard />
                </RequiredAuth>
              }
            >
              <Route path="/dashboard" element={<Profile />} />
              <Route path="/dashboard/profile" element={<Profile />} />
              <Route path="/dashboard/edit-profile" element={<Profile />} />
              <Route path="/dashboard/review" element={<AddReview />} />
              <Route path="/dashboard/my-orders" element={<MyOrders />} />
              <Route
                path="/dashboard/manage-product"
                element={
                  <IsAdmin>
                    <ManageProduct />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/add-product"
                element={
                  <IsAdmin>
                    <AddProduct />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-orders"
                element={
                  <IsAdmin>
                    <ManageOrders />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/make-admin"
                element={
                  <IsAdmin>
                    <AddAdmin />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-reviews"
                element={
                  <IsAdmin>
                    <ManageReviews />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-offers"
                element={
                  <IsAdmin>
                    <ManageOffers />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-banner"
                element={
                  <IsAdmin>
                    <ManageBanner />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/add-offer"
                element={
                  <IsAdmin>
                    <AddOffer />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/pixel-setup"
                element={
                  <IsAdmin>
                    <PixelSetup />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-authors"
                element={
                  <IsAdmin>
                    <ManageAuthors />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-publications"
                element={
                  <IsAdmin>
                    <ManagePublicationsAdmin />
                  </IsAdmin>
                }
              />
              <Route
                path="/dashboard/manage-categories"
                element={
                  <IsAdmin>
                    <ManageCategoriesAdmin />
                  </IsAdmin>
                }
              />
            </Route>
            <Route path="*" element={<NotFoundPage />} />
          </Routes>
        </ScrollToTop>
      </Suspense>
    </AuthProvider>
  );
}

export default App;
